import { useMemo } from 'react';

import type { useNewGroupActionsController } from '@atlassian/aiops-group-common';
import type { NewSNRPredictionType, SNRPredictionType } from '@atlassian/eoc-alert-common';

import commonMessages from '../../../messages';
import type { TGroupLineItemAction } from '../../../types';

type BulkLoadingState = {
	isBulkAcceptAIClassificationLoading?: boolean;
	isBulkAckLoading?: boolean;
	isBulkAssignLoading?: boolean;
	isBulkAssignToMeLoading?: boolean;
	isBulkCloseLoading?: boolean;
	isBulkDeleteLoading?: boolean;
	isBulkSnoozeLoading?: boolean;
	processingBulkIds: string[];
};

export interface UseAllGroupActionsParams {
	bulkLoadingState: BulkLoadingState;
	groupActionsController: ReturnType<typeof useNewGroupActionsController>;
	groupId: string;
	snrClassification?: SNRPredictionType | NewSNRPredictionType;
}

/**
 * Shared hook that generates the complete list of available group alert actions.
 * This centralizes the business logic for creating group action definitions to avoid
 * duplication between different group action components (summary, inline, etc.).
 */
export const useAllGroupActions = ({
	groupId,
	bulkLoadingState,
	groupActionsController,
	snrClassification,
}: UseAllGroupActionsParams): TGroupLineItemAction[] => {
	const { processingBulkIds } = bulkLoadingState;

	const {
		action,
		isCountLoading,
		isAckAllLoading,
		isCloseAllLoading,
		isSnoozeAllLoading,
		isDeleteAllLoading,
		isAssignLoading,
		isAssignToMeLoading,
		isAcceptAIClassificationLoading,
		initiateAckAll,
		initiateCloseAll,
		initiateSnoozeAll,
		initiateDeleteAll,
		initiateAssign,
		initiateAssignToMe,
		initiateAcceptAIClassification,
		performAckAll,
		performCloseAll,
		performSnoozeAll,
		performDeleteAll,
		performAssign,
	} = groupActionsController;

	const isBulkProcessing = useMemo(
		() => processingBulkIds.includes(groupId),
		[processingBulkIds, groupId],
	);

	const allActions: TGroupLineItemAction[] = [
		{
			label: commonMessages.acknowledgeAll,
			onClick: initiateAckAll,
			actionId: 'acknowledgeAll',
			requiresAlertCount: true,
			isLoading:
				(action === 'acknowledgeAll' && isCountLoading) ||
				isAckAllLoading ||
				(isBulkProcessing && bulkLoadingState.isBulkAckLoading),
			onSubmit: performAckAll,
		},
		{
			label: commonMessages.closeAll,
			onClick: initiateCloseAll,
			actionId: 'closeAll',
			requiresAlertCount: true,
			isLoading:
				(action === 'closeAll' && isCountLoading) ||
				isCloseAllLoading ||
				(isBulkProcessing && bulkLoadingState.isBulkCloseLoading),
			onSubmit: performCloseAll,
		},
		{
			label: commonMessages.assign,
			onClick: initiateAssign,
			actionId: 'assign',
			requiresAlertCount: false,
			isLoading: isAssignLoading || (isBulkProcessing && bulkLoadingState.isBulkAssignLoading),
			onSubmit: performAssign,
		},
		{
			label: commonMessages.assignToMe,
			onClick: initiateAssignToMe,
			actionId: 'assignToMe',
			requiresAlertCount: false,
			isLoading:
				isAssignToMeLoading || (isBulkProcessing && bulkLoadingState.isBulkAssignToMeLoading),
			onSubmit: undefined, // This is sync action, no need to perform any onSubmit
		},
		{
			label: commonMessages.snoozeAll,
			onClick: initiateSnoozeAll,
			actionId: 'snooze',
			requiresAlertCount: true,
			isLoading:
				(action === 'snooze' && isCountLoading) ||
				isSnoozeAllLoading ||
				(isBulkProcessing && bulkLoadingState.isBulkSnoozeLoading),
			onSubmit: performSnoozeAll,
		},
		{
			label: commonMessages.deleteAll,
			onClick: initiateDeleteAll,
			actionId: 'delete',
			requiresAlertCount: true,
			isLoading:
				(action === 'delete' && isCountLoading) ||
				isDeleteAllLoading ||
				(isBulkProcessing && bulkLoadingState.isBulkDeleteLoading),
			onSubmit: performDeleteAll,
		},
		{
			label: commonMessages.acceptPrediction,
			onClick: () => initiateAcceptAIClassification({ prediction: snrClassification! }),
			actionId: 'aiPrediction',
			requiresAlertCount: false,
			isLoading:
				isAcceptAIClassificationLoading ||
				(isBulkProcessing && bulkLoadingState.isBulkAcceptAIClassificationLoading),
			onSubmit: undefined, // This is sync action, no need to perform any onSubmit
		},
	];

	return allActions;
};
